import { useState, useEffect } from 'react';
import { IMedic } from '../../types/type';
import { AutoparkService, IAutopark } from '../../services/autopark.service';
import './medicAutoparks.css';

function MedicAutoparks({ medic }: { medic: IMedic }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoparks, setAutoparks] = useState<IAutopark[]>([]);
  const [availableAutoparks, setAvailableAutoparks] = useState<IAutopark[]>([]);
  const [selectedAutoparkId, setSelectedAutoparkId] = useState<string>('');

  useEffect(() => {
    const fetchAutoparks = async () => {
      try {
        setLoading(true);
        setError(null);

        // Получаем автопарки медика через сервис
        const medicAutoparks = await AutoparkService.getMedicAutoparks(medic.id);
        setAutoparks(medicAutoparks);

        // Получаем доступные автопарки через сервис
        const availableAutoparks = await AutoparkService.getAvailableAutoparksForProfile(medic.id, 'medic');
        setAvailableAutoparks(availableAutoparks);
      } catch (error: any) {
        console.error('Ошибка при загрузке автопарков:', error);
        setError(error.message || 'Не удалось загрузить автопарки');
        setAutoparks([]);
        setAvailableAutoparks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAutoparks();
  }, [medic.id]);

  const handleAddAutopark = async () => {
    if (!selectedAutoparkId) return;

    try {
      setLoading(true);

      // Привязываем автопарк к медику через сервис
      await AutoparkService.linkProfileToAutopark(selectedAutoparkId, medic.id);

      // Обновляем список автопарков
      const selectedAutopark = availableAutoparks.find((ap) => ap.id === selectedAutoparkId);
      if (selectedAutopark) {
        setAutoparks([...autoparks, selectedAutopark]);
        setAvailableAutoparks(availableAutoparks.filter((ap) => ap.id !== selectedAutoparkId));
      }

      setSelectedAutoparkId('');
    } catch (error: any) {
      console.error('Ошибка при добавлении автопарка:', error);
      setError(error.message || 'Не удалось добавить автопарк');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAutopark = async (autoparkId: string) => {
    try {
      setLoading(true);

      // Отвязываем автопарк от медика через сервис
      await AutoparkService.unlinkProfileFromAutopark(autoparkId, medic.id);

      // Обновляем списки автопарков
      const removedAutopark = autoparks.find((ap) => ap.id === autoparkId);
      if (removedAutopark) {
        setAutoparks(autoparks.filter((ap) => ap.id !== autoparkId));
        setAvailableAutoparks([...availableAutoparks, removedAutopark]);
      }
    } catch (error: any) {
      console.error('Ошибка при удалении автопарка:', error);
      setError(error.message || 'Не удалось удалить автопарк');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="medic-autoparks">
      <div className="medic-autoparks__info">
        <p>
          {medic.autoparkEmployee
            ? 'Медик является сотрудником автопарка и обслуживает только свой автопарк.'
            : 'Медик является нашим сотрудником и может обслуживать несколько автопарков.'}
        </p>
      </div>

      {/* Форма добавления нового автопарка - только для "наших" медиков */}
      {!medic.autoparkEmployee && (
        <div className="medic-autoparks__add-form">
          <h3>Добавить автопарк</h3>
          <div className="medic-autoparks__form-content">
            <select
              className="medic-autoparks__select"
              value={selectedAutoparkId}
              onChange={(e) => setSelectedAutoparkId(e.target.value)}
            >
              <option value="">Выберите автопарк</option>
              {availableAutoparks.map((autopark) => (
                <option key={autopark.id} value={autopark.id}>
                  {autopark.tradeName || autopark.legalName}
                </option>
              ))}
            </select>
            <button
              className="medic-autoparks__add-btn"
              onClick={handleAddAutopark}
              disabled={!selectedAutoparkId || loading}
            >
              Добавить
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="medic-autoparks__loading">Загрузка автопарков...</div>
      ) : error ? (
        <div className="medic-autoparks__error">{error}</div>
      ) : (
        <>
          {/* Список текущих автопарков */}
          <div className="medic-autoparks__content">
            {autoparks.length === 0 ? (
              <div className="medic-autoparks__empty">Нет доступных автопарков</div>
            ) : (
              <table className="medic-autoparks__table">
                <thead>
                  <tr>
                    <th>Название</th>
                    <th>Юридическое название</th>

                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {autoparks.map((autopark) => (
                    <tr key={autopark.id}>
                      <td>{autopark.tradeName}</td>
                      <td>{autopark.legalName}</td>

                      <td>
                        {!medic.autoparkEmployee && (
                          <button
                            className="medic-autoparks__remove-btn"
                            onClick={() => handleRemoveAutopark(autopark.id)}
                          >
                            Удалить
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default MedicAutoparks;
